import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useRag } from "../../lib/rag/useRag";
import { QueryType } from "../../lib/rag/ragTypes";
import ApiKeyInput from "../../components/ui/ApiKeyInput";
import RagDebugger from "../../components/ui/RagDebugger";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";

interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  sources?: Array<{ title: string; sku_id: string }>;
  timestamp: Date;
  queryType?: QueryType;
}

export const RAWPAAIPage = (): JSX.Element => {
  const navigate = useNavigate();
  const auth = getAuth();
  const { settings } = useSystemSettings();
  const [userName, setUserName] = useState<string>("");
  const [targetDomain, setTargetDomain] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [userInput, setUserInput] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      content: 'Welcome to RAWPA AI! Ask me any cybersecurity question, and I\'ll provide insights based on the security knowledge base. I can help with techniques, payloads, tool recommendations, and more.',
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [messageType, setMessageType] = useState<QueryType>(QueryType.GENERAL);
  const [showQueryTypeSelector, setShowQueryTypeSelector] = useState(false);
  const [detectedQueryType, setDetectedQueryType] = useState<QueryType>(QueryType.GENERAL);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState<boolean>(false);
  const [showDebugger, setShowDebugger] = useState<boolean>(false);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { 
    loading: ragLoading, 
    initialized: ragInitialized, 
    error: ragError, 
    result: ragResult, 
    query,
    setApiKey,
    analyzeQueryType,
    initialize,
    debugInfo
  } = useRag();
  
  // Authentication effect
  useEffect(() => {
    // Check if AI is disabled
    if (settings.disableAI) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const displayName = user.displayName || user.email?.split('@')[0] || 'User';
        setUserName(displayName);
        localStorage.setItem('rawpa_user_name', displayName);
      } else {
        navigate('/signin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, auth, settings.disableAI]);
  
  // Effect to scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Effect to initialize RAG system
  useEffect(() => {
    // Initialize RAG
    initialize().catch(error => {
      console.error("Failed to initialize RAG:", error);
      // Show API key modal if not already shown
      setApiKeyModalOpen(true);
    });
  }, [initialize]);
  
  // Effect to handle RAG result
  useEffect(() => {
    if (ragResult && !ragLoading) {
      setMessages(prev => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          content: ragResult.answer,
          sender: 'ai',
          sources: ragResult.sources,
          timestamp: new Date()
        }
      ]);
    }
  }, [ragResult, ragLoading]);
  
  // Effect to show API key modal if RAG error or not initialized
  useEffect(() => {
    if (ragError && ragError.includes("API key")) {
      setApiKeyModalOpen(true);
    }
  }, [ragError]);
  
  // Effect to analyze query type as user types
  useEffect(() => {
    if (userInput.trim().length > 10) {
      const suggestedType = analyzeQueryType(userInput);
      setDetectedQueryType(suggestedType);
    } else {
      setDetectedQueryType(QueryType.GENERAL);
    }
  }, [userInput, analyzeQueryType]);
  
  // Handle user input submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!userInput.trim()) return;
    
    // Pick the query type (either manually selected or auto-detected)
    const finalQueryType = showQueryTypeSelector ? messageType : detectedQueryType;
    
    // Check if this is an XSS WAF bypass query
    const isXssWafQuery = 
      (finalQueryType === QueryType.PAYLOAD || userInput.toLowerCase().includes('payload')) &&
      (userInput.toLowerCase().includes('xss') || userInput.toLowerCase().includes('cross-site')) &&
      (userInput.toLowerCase().includes('waf') || 
       userInput.toLowerCase().includes('bypass') || 
       userInput.toLowerCase().includes('filter') ||
       userInput.toLowerCase().includes('evade') ||
       userInput.toLowerCase().includes('got through') ||
       userInput.toLowerCase().includes('blocked'));
    
    // Add user message
    const newMessage: Message = {
      id: `user-${Date.now()}`,
      content: userInput,
      sender: 'user',
      timestamp: new Date(),
      queryType: finalQueryType
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    // Add loading message
    const loadingId = `loading-${Date.now()}`;
    setMessages(prev => [...prev, {
      id: loadingId,
      content: isXssWafQuery ? 
        'Generating XSS payload with Hugging Face AI (Qwen/Qwen3-235B-A22B model)...' : 
        '...',
      sender: 'ai',
      timestamp: new Date()
    }]);
    
    try {
      // Check if RAG is initialized
      if (!ragInitialized) {
        const success = await initialize();
        if (!success) {
          throw new Error("RAG system is not initialized. Please check your connection and try again.");
        }
      }
      
      // Process the message with RAG
      // Include target domain in the query if it exists
      const enhancedQuery = targetDomain ? 
        `[Target Domain: ${targetDomain}] ${userInput}` : 
        userInput;
      
      const result = await query(enhancedQuery, finalQueryType);
      
      if (!result) {
        throw new Error("Failed to get a response from the RAG system.");
      }
      
      // Remove the loading message
      setMessages(prev => prev.filter(m => m.id !== loadingId));
      
      // Add AI response
      setMessages(prev => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          content: result.answer,
          sender: 'ai',
          sources: result.sources,
          timestamp: new Date()
        }
      ]);
    } catch (error) {
      console.error("Error processing message:", error);
      
      // Remove the loading message
      setMessages(prev => prev.filter(m => m.id !== loadingId));
      
      // Add error message
      setMessages(prev => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          content: `Error: ${error instanceof Error ? error.message : 'Failed to process your request'}. Please try again or check your API key.`,
          sender: 'ai',
          timestamp: new Date()
        }
      ]);
    }
    
    // Clear input
    setUserInput("");
    setShowQueryTypeSelector(false);
  };
  
  // Handle API key set callback
  const handleApiKeySet = (apiKey: string) => {
    setApiKey(apiKey);
    setApiKeyModalOpen(false);
    
    // Re-initialize RAG with the new API key
    initialize({ apiKey }).catch(error => {
      console.error("Failed to initialize RAG with new API key:", error);
    });
  };
  
  // Handle target domain change
  const handleTargetDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const domain = e.target.value;
    setTargetDomain(domain);
    localStorage.setItem('rawpa_target_domain', domain);
  };
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Toggle debugger visibility
  const toggleDebugger = () => {
    setShowDebugger(prev => !prev);
  };
  
  // Format message content by replacing placeholders and enhancing structure
  const formatMessage = (content: string): JSX.Element | string => {
    if (!content) return '';
    
    // Replace target domain placeholders
    let formattedContent = targetDomain 
      ? content.replace(/<target_domain>/g, targetDomain)
      : content;
    
    // Check if this is an AI response with structured sections
    if (formattedContent.includes('SUMMARY:') || 
        formattedContent.includes('DETAILED EXPLANATION:') || 
        formattedContent.includes('EXAMPLE(S):') || 
        formattedContent.includes('CONCLUSION:')) {
      
      // Split the content into sections
      const sections = formattedContent.split(/(\d\.\s+(?:SUMMARY|DETAILED EXPLANATION|EXAMPLE\(S\)|CONCLUSION):)/g);
      
      // Create formatted JSX
      return (
        <div className="text-left">
          {sections.map((section, index) => {
            // Check if this is a section header
            if (/^\d\.\s+(SUMMARY|DETAILED EXPLANATION|EXAMPLE\(S\)|CONCLUSION):$/.test(section)) {
              return (
                <h3 key={index} className="font-bold text-sm mt-3 mb-1 text-blue-800">
                  {section}
                </h3>
              );
            } else if (section.trim()) {
              // Process code blocks
              const parts = section.split(/(```[\s\S]*?```)/g);
              
              return (
                <div key={index} className="mb-2">
                  {parts.map((part, partIndex) => {
                    if (part.startsWith('```') && part.endsWith('```')) {
                      // Extract language if specified
                      let language = 'plaintext';
                      const match = part.match(/^```(\w+)/);
                      if (match) language = match[1];
                      
                      // Extract code content
                      const code = part.replace(/^```\w*\n/, '').replace(/```$/, '');
                      
                      return (
                        <pre key={partIndex} className="bg-gray-800 text-gray-100 p-2 rounded-md text-xs overflow-x-auto my-2">
                          <code>{code}</code>
                        </pre>
                      );
                    } else {
                      // Process lists
                      const listItems = part.split(/\n(?=[-*•]\s+)/g);
                      
                      return (
                        <div key={partIndex}>
                          {listItems.map((item, itemIndex) => {
                            if (item.trim().match(/^[-*•]\s+/)) {
                              return (
                                <div key={itemIndex} className="flex items-start my-1">
                                  <span className="mr-2">•</span>
                                  <span>{item.replace(/^[-*•]\s+/, '')}</span>
                                </div>
                              );
                            } else {
                              // Handle numbered lists
                              const numberedItems = item.split(/\n(?=\d+\.\s+)/g);
                              
                              return (
                                <div key={itemIndex}>
                                  {numberedItems.map((nItem, nIndex) => {
                                    if (nItem.trim().match(/^\d+\.\s+/)) {
                                      const match = nItem.match(/^\d+\./);
                                      const numberPrefix = match ? match[0] : '#.';
                                      
                                      return (
                                        <div key={nIndex} className="flex items-start my-1">
                                          <span className="mr-2 font-medium">{numberPrefix}</span>
                                          <span>{nItem.replace(/^\d+\.\s+/, '')}</span>
                                        </div>
                                      );
                                    } else {
                                      return <p key={nIndex} className="my-1">{nItem}</p>;
                                    }
                                  })}
                                </div>
                              );
                            }
                          })}
                        </div>
                      );
                    }
                  })}
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }
    
    // For regular messages without structured formatting
    return formattedContent;
  };

  // Function to get query type label
  const getQueryTypeLabel = (type: QueryType): string => {
    switch(type) {
      case QueryType.PAYLOAD: return "🧪 Payload";
      case QueryType.TECHNIQUE: return "📝 Technique";
      case QueryType.TOOL: return "🔧 Tool";
      case QueryType.WAF_BYPASS: return "🛡️ WAF Bypass";
      default: return "General";
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="font-['Space_Grotesk'] text-black"
              >
                Back to Dashboard
              </Button>
              <div 
                onClick={handleSignOut}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-8">
        {/* AI Disabled Alert */}
        {settings.disableAI && (
          <Alert className="mb-8 bg-yellow-50 border-yellow-200">
            <AlertTitle className="text-yellow-800 font-semibold">AI Features Disabled</AlertTitle>
            <AlertDescription className="text-yellow-700">
              <p className="mb-4">
                AI features are currently disabled by the administrator. Please check back later or contact support for assistance.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')}
                className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
              >
                Return to Dashboard
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!settings.disableAI && (
          <>
            <div className="bg-white rounded-[20px] p-6 shadow-lg mb-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <div>
                  <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black">
                    RAWPA AI Assistant
                  </h1>
                  <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                    Ask any cybersecurity question
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Target domain (optional)"
                      className="h-10 px-3 rounded-lg border border-gray-300 text-sm w-[200px]"
                      value={targetDomain}
                      onChange={handleTargetDomainChange}
                    />
                  </div>
                  <Button
                    variant="outline"
                    className="h-10 text-xs"
                    onClick={() => setApiKeyModalOpen(true)}
                  >
                    API Key
                  </Button>
                  <Button
                    variant="outline"
                    className="h-10 text-xs"
                    onClick={toggleDebugger}
                  >
                    {showDebugger ? "Hide Debug" : "Debug"}
                  </Button>
                </div>
              </div>
              
              {ragError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4">
                  <p className="text-sm font-medium">Error: {ragError}</p>
                  <p className="text-xs mt-1">
                    {ragError.includes("API key") ? 
                      "Please set your API key to use the AI assistant." : 
                      "Please try again or contact support if the issue persists."}
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className={`lg:col-span-${showDebugger ? '3' : '4'}`}>
                <div className="bg-white rounded-[20px] p-6 shadow-lg">
                  <div className="h-[500px] overflow-y-auto mb-4 space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender === "user" ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[80%] p-4 rounded-[20px] ${
                            message.sender === "user"
                              ? "bg-[#daed6e] text-black"
                              : "bg-[#f5f5f5] text-black"
                          }`}
                        >
                          {message.sender === "ai" && message.id === "welcome" ? (
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-bold">RAWPA AI</span>
                              <span className="bg-[#daed6e] text-xs px-2 py-0.5 rounded">
                                Assistant
                              </span>
                            </div>
                          ) : null}
                          
                          <div className="prose prose-sm max-w-none">
                            {formatMessage(message.content)}
                          </div>
                          
                          {message.sources && message.sources.length > 0 && (
                            <div className="mt-3 pt-2 border-t border-gray-200">
                              <p className="text-xs text-gray-500 mb-1">Sources:</p>
                              <ul className="list-disc pl-4 text-xs text-gray-600">
                                {message.sources.map((source, idx) => (
                                  <li key={idx}>{source.title}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {message.queryType && (
                            <div className="mt-2">
                              <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                                {getQueryTypeLabel(message.queryType)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  <form onSubmit={handleSubmit} className="relative">
                    <input
                      type="text"
                      placeholder="Ask a cybersecurity question..."
                      className="w-full h-12 px-4 pr-[120px] rounded-lg border border-gray-300"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      disabled={ragLoading || !ragInitialized}
                    />
                    
                    {userInput.length > 0 && (
                      <div className="absolute right-16 top-[50%] transform translate-y-[-50%] flex items-center">
                        <button
                          type="button"
                          className={`text-xs px-2 py-1 rounded ${
                            showQueryTypeSelector ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                          }`}
                          onClick={() => setShowQueryTypeSelector(!showQueryTypeSelector)}
                        >
                          {getQueryTypeLabel(showQueryTypeSelector ? messageType : detectedQueryType)}
                        </button>
                      </div>
                    )}
                    
                    <button
                      type="submit"
                      className="absolute right-2 top-[50%] transform translate-y-[-50%] w-10 h-10 bg-[#daed6e] rounded-lg flex items-center justify-center disabled:opacity-50"
                      disabled={!userInput.trim() || ragLoading || !ragInitialized}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    </button>
                  </form>
                  
                  {showQueryTypeSelector && (
                    <div className="mt-2 p-2 bg-white rounded-lg border border-gray-200 shadow-sm">
                      <p className="text-xs text-gray-500 mb-1">Select query type:</p>
                      <div className="flex flex-wrap gap-1">
                        {Object.values(QueryType).map((type) => (
                          <button
                            key={type}
                            type="button"
                            className={`text-xs px-2 py-1 rounded ${
                              messageType === type ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                            }`}
                            onClick={() => setMessageType(type)}
                          >
                            {getQueryTypeLabel(type)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {!ragInitialized && !ragLoading && !ragError && (
                    <p className="text-center text-xs text-gray-500 mt-2">
                      Initializing AI system...
                    </p>
                  )}
                </div>
              </div>
              
              {showDebugger && (
                <div className="lg:col-span-1">
                  <RagDebugger />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* API Key Modal */}
      <ApiKeyInput 
        isOpen={apiKeyModalOpen} 
        onClose={() => setApiKeyModalOpen(false)} 
        onApiKeySet={handleApiKeySet}
      />
    </div>
  );
}; 